import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, LoadingSkeleton, Pagination, EmptyState } from '../components/ui';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const ACTION_VARIANT = {
  create: 'success',
  update: 'info',
  delete: 'danger',
  impersonation_start: 'warning',
  impersonation_end: 'warning',
};

function formatResourceType(type) {
  return type ? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
}

function SuperAdminAuditLog({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const pageSize = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/superadmin/audit-logs?page=${page}&page_size=${pageSize}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotalPages(data.total_pages);
      } else {
        setError('Failed to load audit log');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const columns = [
    { header: 'When', render: (row) => <span style={{ whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleString()}</span> },
    { header: 'School', render: (row) => row.school_id ? `#${row.school_id}` : <em>platform</em> },
    {
      header: 'Actor',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.username || 'System'}</div>
          {row.impersonator_id && <div style={{ fontSize: '0.75rem', color: 'var(--color-warning, #D97706)' }}>via impersonation (admin #{row.impersonator_id})</div>}
        </div>
      ),
    },
    { header: 'Action', render: (row) => <Badge variant={ACTION_VARIANT[row.action] || 'default'}>{row.action}</Badge> },
    { header: 'Resource', render: (row) => formatResourceType(row.resource_type) },
    { header: 'Details', render: (row) => row.resource_label || (row.resource_id ? `#${row.resource_id}` : '—') },
    {
      header: '',
      render: (row) => row.details && (
        <button
          onClick={() => setExpanded(expanded === row.id ? null : row.id)}
          style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
        >
          {expanded === row.id ? 'Hide' : 'View'}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Platform Audit Log</h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
          Cross-tenant activity, including impersonation sessions.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={10} />
        ) : logs.length === 0 ? (
          <EmptyState title="No activity recorded yet" message="Platform-wide actions will show up here." icon={<ClipboardDocumentListIcon />} />
        ) : (
          <>
            <Table columns={columns} data={logs} />
            {logs.filter((l) => l.id === expanded).map((l) => (
              <pre
                key={l.id}
                style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--color-bg)', borderRadius: 'var(--radius)', fontSize: '0.75rem', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {JSON.stringify(JSON.parse(l.details), null, 2)}
              </pre>
            ))}
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}

export default SuperAdminAuditLog;
